import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Link2, Loader2, Mail, RefreshCw, RotateCcw } from "lucide-react";
import { API, api, copyText, formatApiError, shareUrl } from "../lib/api";

const btn =
  "inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-[background-color] duration-200 hover:bg-slate-50 active:scale-95 disabled:opacity-50";
const primary =
  "inline-flex items-center gap-2 rounded-lg bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-[background-color] duration-200 hover:bg-blue-800 active:scale-95 disabled:opacity-60";

const escapeAttr = (s) => s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const emailHtml = (event) =>
  `<a href="${shareUrl(event.slug)}"><img src="${event.gif_url}" alt="${escapeAttr(event.occasion)} – click to RSVP" width="600"></a>`;

async function copyRichHtml(html, plain) {
  try {
    await navigator.clipboard.write([
      new ClipboardItem({ "text/html": new Blob([html], { type: "text/html" }), "text/plain": new Blob([plain], { type: "text/plain" }) }),
    ]);
  } catch {
    const el = document.createElement("div");
    el.contentEditable = "true";
    el.innerHTML = html;
    el.style.position = "fixed";
    el.style.left = "-9999px";
    document.body.appendChild(el);
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    document.execCommand("copy");
    sel.removeAllRanges();
    el.remove();
  }
}

export const EmailGifPanel = ({ event: initial }) => {
  const [event, setEvent] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [replay, setReplay] = useState(0);
  const updating = Boolean(event.gif_url) && new Date(event.updated_at) > new Date(event.gif_updated_at);
  // Preview/download from this environment's backend; the copied email HTML keeps the public production URL.
  const localGif = event.gif_url ? `${API}/public/events/${event.slug}/invite.gif${new URL(event.gif_url).search}` : null;

  useEffect(() => {
    if (!updating) return;
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      if (tries > 20) return clearInterval(timer);
      try {
        const { data } = await api.get(`/events/${event.id}`);
        setEvent(data);
      } catch {
        clearInterval(timer);
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [updating, event.id]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${event.id}/gif`);
      setEvent(data);
      setReplay((n) => n + 1);
      toast.success("Email GIF ready");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyEmail = async () => {
    await copyRichHtml(emailHtml(event), shareUrl(event.slug));
    toast.success("Copied! Paste it into your email body.");
  };

  const copyLink = async () => {
    await copyText(shareUrl(event.slug));
    toast.success("Invite link copied");
  };

  return (
    <section data-testid="email-gif-panel" className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex-1">
          <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-700"><Mail className="h-4 w-4" /> Email invitation</p>
          <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">Animated envelope GIF</h2>
          <p className="mt-2 max-w-xl text-sm text-slate-600">
            An envelope opens and your invitation slides out (about 3 seconds, plays once). Clicking the image in the email opens your RSVP page.
          </p>
          {!event.gif_url ? (
            <button data-testid="gif-create-btn" onClick={generate} disabled={busy} className={`${primary} mt-6`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Create email GIF
            </button>
          ) : (
            <>
              <div className="mt-6 flex flex-wrap gap-2">
                <button data-testid="gif-copy-email-btn" onClick={copyEmail} className={primary}><Copy className="h-4 w-4" /> Copy for email</button>
                <a data-testid="gif-download-btn" href={`${localGif}&download=1`} download className={btn}><Download className="h-4 w-4" /> Download GIF</a>
                <button data-testid="gif-copy-link-btn" onClick={copyLink} className={btn}><Link2 className="h-4 w-4" /> Copy link</button>
                <button data-testid="gif-regenerate-btn" onClick={generate} disabled={busy || updating} className={btn}>
                  {busy || updating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Regenerate
                </button>
              </div>
              <p data-testid="gif-paste-instructions" className="mt-4 text-sm font-semibold text-slate-800">Paste into your email body (Ctrl/Cmd+V).</p>
              <p className="mt-1 text-xs text-slate-500">
                Tip: some Outlook desktop versions show only the first frame, a closed envelope with your occasion name on it.
                {updating && <span data-testid="gif-updating-note" className="ml-1 font-semibold text-blue-700">Updating GIF with your latest edits…</span>}
              </p>
              {event.password_enabled && (
                <p data-testid="gif-password-note" className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                  The GIF shows your invitation design; the RSVP page still requires the password.
                </p>
              )}
              <details className="mt-4 text-xs text-slate-500">
                <summary className="cursor-pointer font-semibold">Show HTML snippet</summary>
                <textarea data-testid="gif-email-html" readOnly value={emailHtml(event)} rows={3} className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 p-2 font-mono text-xs" />
              </details>
            </>
          )}
        </div>
        {event.gif_url && (
          <div className="w-full max-w-[300px] shrink-0">
            <img key={`${localGif}-${replay}`} data-testid="gif-preview-img" src={`${localGif}&r=${replay}`} alt={`${event.occasion} – click to RSVP`} className="min-h-[240px] w-full rounded-xl border border-slate-200 bg-slate-50" />
            <button data-testid="gif-replay-btn" onClick={() => setReplay((n) => n + 1)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-blue-700">
              <RotateCcw className="h-3.5 w-3.5" /> Replay
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
